import { baseApi } from './baseApi';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  ApiResponse
} from '../../types/api';

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listUsers: builder.query<User[], { includeInactive?: boolean }>({
      query: ({ includeInactive = false }) => ({
        url: '/users',
        params: { include_inactive: includeInactive },
      }),
      transformResponse: (response: ApiResponse<User[]>) => response.data || response,
      providesTags: ['Users'],
    }),

    createUser: builder.mutation<User, CreateUserRequest>({
      query: (data) => ({
        url: '/users',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<User>) => response.data || response,
      invalidatesTags: ['Users'],
    }),

    updateUser: builder.mutation<User, { username: string; data: UpdateUserRequest }>({
      query: ({ username, data }) => ({
        url: `/users/${username}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<User>) => response.data || response,
      invalidatesTags: (_result, _error, { username }) => [
        'Users',
        { type: 'Users', id: username },
      ],
    }),

    changePassword: builder.mutation<void, { username: string; data: ChangePasswordRequest }>({
      query: ({ username, data }) => ({
        url: `/users/${username}/password`,
        method: 'PUT',
        body: data,
      }),
    }),

    deactivateUser: builder.mutation<void, string>({
      query: (username) => ({
        url: `/users/${username}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, username) => [
        'Users',
        { type: 'Users', id: username },
      ],
    }),

    reactivateUser: builder.mutation<User, string>({
      query: (username) => ({
        url: `/users/${username}/reactivate`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<User>) => response.data || response,
      invalidatesTags: (_result, _error, username) => [
        'Users',
        { type: 'Users', id: username },
      ],
    }),
  }),
});

export const {
  useListUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
} = userApi;
